import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useServerWebSocket } from '../lib/ws.js';
import { ServerDetail, ServerState, ServerResourceMetrics } from '@kcdash/shared';
import { StatusBadge } from '../components/StatusBadge.js';
import {
  Server,
  Play,
  Square,
  RotateCw,
  Skull,
  Users,
  Cpu,
  HardDrive,
  Clock,
  Terminal,
  Activity,
  Layers,
  Database,
  Network,
  Copy,
  Check,
  FileCode,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export const ServerOverviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [server, setServer] = useState<ServerDetail | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<ServerResourceMetrics[]>([]);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('1h');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const { logs } = useServerWebSocket({
    serverId: id,
    onStatusChange: (newState) => {
      setServer((prev) => (prev ? { ...prev, state: newState } : null));
    },
  });

  const loadServerData = async () => {
    if (!id) return;
    try {
      const [detail, metrics] = await Promise.all([
        api.getServer(id),
        api.getMetrics(id, timeRange),
      ]);
      setServer(detail);
      setMetricsHistory(metrics);
    } catch (err) {
      console.error('Failed to load server data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadServerData();
    const interval = setInterval(loadServerData, 15000);
    return () => clearInterval(interval);
  }, [id, timeRange]);

  const handleStart = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await api.startServer(id);
      await loadServerData();
    } catch (err: any) {
      alert(`Start failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await api.stopServer(id);
      await loadServerData();
    } catch (err: any) {
      alert(`Stop failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestart = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await api.restartServer(id);
      await loadServerData();
    } catch (err: any) {
      alert(`Restart failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceStop = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to force kill the process? Unsaved in-game data may be lost.')) return;
    setActionLoading(true);
    try {
      await api.forceStopServer(id);
      await loadServerData();
    } catch (err: any) {
      alert(`Force stop failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyConnectAddress = () => {
    if (!server) return;
    const address = `${server.domain || server.host}:${server.port}`;
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatUptime = (seconds: number) => {
    if (!seconds) return 'Offline';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  if (isLoading || !server) {
    return (
      <div className="py-20 text-center text-neutral-500 font-mono text-sm">
        Loading server details...
      </div>
    );
  }

  const chartData = metricsHistory.map((m) => ({
    time: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    cpu: m.cpuPercent,
    ram: Math.round(m.memoryBytes / (1024 * 1024)),
  }));

  const connectionAddress = `${server.domain || server.host}:${server.port}`;

  return (
    <div className="space-y-6">
      {/* Top Banner KPI Cards */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-panel-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-panel-subtle border border-panel-border flex items-center justify-center">
              <Server className="w-6 h-6 text-brand-400" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-neutral-100">{server.name}</h2>
                <StatusBadge state={server.state} size="md" />
              </div>
              <p className="text-xs text-neutral-400 font-mono mt-0.5">
                {connectionAddress} • PID: {server.process.pid || 'None'} • Map: {server.map}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleStart}
              disabled={actionLoading || server.state === ServerState.ONLINE || server.state === ServerState.STARTING}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-800 text-emerald-300 disabled:opacity-40 transition shadow-sm"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>START</span>
            </button>

            <button
              onClick={handleStop}
              disabled={actionLoading || server.state === ServerState.OFFLINE || server.state === ServerState.STOPPING}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-red-950/80 hover:bg-red-900 border border-red-800 text-red-300 disabled:opacity-40 transition shadow-sm"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
              <span>STOP</span>
            </button>

            <button
              onClick={handleRestart}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-panel-subtle hover:bg-panel-cardHover border border-panel-border text-neutral-200 disabled:opacity-40 transition shadow-sm"
            >
              <RotateCw className={`w-3.5 h-3.5 ${actionLoading ? 'animate-spin' : ''}`} />
              <span>RESTART</span>
            </button>

            <button
              onClick={handleForceStop}
              disabled={actionLoading || server.state === ServerState.OFFLINE}
              title="Force Kill Process (SIGKILL)"
              className="p-2 rounded-lg text-xs bg-panel-subtle hover:bg-red-950/60 border border-panel-border text-neutral-400 hover:text-red-400 transition"
            >
              <Skull className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 4 Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6">
          <div className="bg-panel-subtle p-4 rounded-xl border border-panel-border/60">
            <div className="flex items-center gap-2 text-neutral-400 text-xs font-medium mb-1">
              <Users className="w-4 h-4 text-brand-400" />
              <span>Players Online</span>
            </div>
            <div className="text-xl font-bold font-mono text-neutral-100">
              {server.process.currentPlayers} <span className="text-xs text-neutral-500 font-normal">/ {server.maxPlayers}</span>
            </div>
          </div>

          <div className="bg-panel-subtle p-4 rounded-xl border border-panel-border/60">
            <div className="flex items-center gap-2 text-neutral-400 text-xs font-medium mb-1">
              <Cpu className="w-4 h-4 text-brand-400" />
              <span>CPU Load</span>
            </div>
            <div className="text-xl font-bold font-mono text-neutral-100">
              {server.process.cpuPercent.toFixed(1)} <span className="text-xs text-neutral-500 font-normal">%</span>
            </div>
          </div>

          <div className="bg-panel-subtle p-4 rounded-xl border border-panel-border/60">
            <div className="flex items-center gap-2 text-neutral-400 text-xs font-medium mb-1">
              <HardDrive className="w-4 h-4 text-brand-400" />
              <span>RAM Memory</span>
            </div>
            <div className="text-xl font-bold font-mono text-neutral-100">
              {server.process.memoryUsageMb} <span className="text-xs text-neutral-500 font-normal">MB</span>
            </div>
          </div>

          <div className="bg-panel-subtle p-4 rounded-xl border border-panel-border/60">
            <div className="flex items-center gap-2 text-neutral-400 text-xs font-medium mb-1">
              <Clock className="w-4 h-4 text-brand-400" />
              <span>Uptime</span>
            </div>
            <div className="text-xl font-bold font-mono text-neutral-100">
              {formatUptime(server.process.uptimeSeconds)}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Connect & Server Details Card */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-panel-border">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-brand-950/70 border border-brand-800 text-brand-400">
              <Network className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-neutral-400 uppercase font-mono tracking-wider">
                Direct Connection Address
              </div>
              <div className="text-base font-bold text-neutral-100 font-mono flex items-center gap-2 mt-0.5">
                <span>{connectionAddress}</span>
                <button
                  onClick={copyConnectAddress}
                  className="p-1 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                  title="Copy connect address"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to={`/servers/${id}/network`}
              className="px-3.5 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition flex items-center gap-1.5"
            >
              <Network className="w-3.5 h-3.5 text-brand-400" />
              <span>Network Settings</span>
            </Link>

            <Link
              to={`/servers/${id}/files?open=server.toml`}
              className="px-3.5 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition flex items-center gap-1.5"
            >
              <FileCode className="w-3.5 h-3.5 text-brand-400" />
              <span>Edit server.toml</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 text-xs font-mono">
          <div>
            <span className="text-neutral-500 block mb-0.5">MAP / LEVEL</span>
            <span className="text-neutral-200 font-semibold">{server.map}</span>
          </div>
          <div>
            <span className="text-neutral-500 block mb-0.5">GAMEMODE</span>
            <span className="text-neutral-200 font-semibold">{server.gamemode || 'freeroam.lua'}</span>
          </div>
          <div>
            <span className="text-neutral-500 block mb-0.5">GAME PORT</span>
            <span className="text-brand-400 font-semibold">{server.port} (UDP/TCP)</span>
          </div>
          <div>
            <span className="text-neutral-500 block mb-0.5">AUTO RESTART</span>
            <span className="text-emerald-400 font-semibold">{server.autoRestart ? 'ENABLED' : 'DISABLED'}</span>
          </div>
        </div>
      </div>

      {/* Metrics Charts */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-brand-400" />
            <h3 className="text-sm font-semibold text-neutral-200">Historical Resource Usage</h3>
          </div>

          <div className="flex items-center gap-1 bg-panel-subtle border border-panel-border p-0.5 rounded-lg text-xs font-mono">
            {(['1h', '24h', '7d'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setTimeRange(r)}
                className={`px-2.5 py-1 rounded transition ${
                  timeRange === r ? 'bg-brand-600 text-white font-medium' : 'text-neutral-400 hover:text-neutral-200'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="ramGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="time" stroke="#525252" fontSize={10} tickLine={false} />
              <YAxis stroke="#525252" fontSize={10} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  borderColor: '#27272a',
                  borderRadius: '8px',
                  fontSize: '11px',
                }}
              />
              <Area type="monotone" dataKey="cpu" stroke="#22c55e" fillOpacity={1} fill="url(#cpuGradient)" name="CPU %" />
              <Area type="monotone" dataKey="ram" stroke="#3b82f6" fillOpacity={1} fill="url(#ramGradient)" name="RAM (MB)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Live Console Sneak Peek & System status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-brand-400" />
            <h3 className="text-sm font-semibold text-neutral-200">Server Environment & Runtime</h3>
          </div>

          <div className="space-y-3 text-xs font-mono">
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-panel-subtle border border-panel-border/50">
              <span className="text-neutral-400">Binary Architecture</span>
              <span className="text-neutral-200">Linux x64 (Native ELF + .NET 10)</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-panel-subtle border border-panel-border/50">
              <span className="text-neutral-400">Networking Stack</span>
              <span className="text-neutral-200">ENet (UDP) & TCP Relay</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-panel-subtle border border-panel-border/50">
              <span className="text-neutral-400">Server Directory</span>
              <span className="text-neutral-200 truncate max-w-xs">{server.serverDirectory}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-panel-subtle border border-panel-border/50">
              <span className="text-neutral-400">Database Engine</span>
              <span className="text-neutral-200 flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5 text-brand-400" />
                {server.databaseStatus === 'connected' ? 'Configured & Active' : 'Managed in Database Tab'}
              </span>
            </div>
          </div>
        </div>

        {/* Live Console Sneak Peek */}
        <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-brand-400" />
                <h3 className="text-sm font-semibold text-neutral-200">Live Console Feed</h3>
              </div>
              <Link
                to={`/servers/${id}/console`}
                className="text-xs font-semibold text-brand-400 hover:text-brand-300 transition"
              >
                Full Console &rarr;
              </Link>
            </div>

            <div className="bg-[#0c0f0e] border border-panel-border rounded-lg p-3 font-mono text-[11px] h-48 overflow-y-auto space-y-1 console-output">
              {logs.length === 0 ? (
                <div className="text-neutral-600">Waiting for server output stream...</div>
              ) : (
                logs.slice(-15).map((l, idx) => (
                  <div
                    key={idx}
                    className={`truncate ${
                      l.level === 'error'
                        ? 'text-red-400'
                        : l.level === 'warn'
                        ? 'text-amber-400'
                        : 'text-neutral-300'
                    }`}
                  >
                    <span className="text-neutral-600">[{new Date(l.timestamp).toLocaleTimeString()}]</span>{' '}
                    {l.message}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-panel-border flex items-center justify-between text-xs text-neutral-500 font-mono">
            <span>STDOUT / STDERR STREAM</span>
            <span className="flex items-center gap-1.5 text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse" />
              CONNECTED
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
